import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Slide,
} from '@mui/material';

const FeedbackContext = createContext();

export const useFeedback = () => useContext(FeedbackContext);

const TransitionUp = (props) => {
  return <Slide {...props} direction="up" />;
};

export const FeedbackProvider = ({ children }) => {
  // --- TOAST STATE ---
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'error' | 'warning' | 'info'
  });

  // --- CONFIRM DIALOG STATE ---
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    danger: false, // if true, makes the confirm button red (error color)
    onConfirm: () => {},
    onCancel: () => {},
  });

  // --- ACTIONS ---
  const showToast = useCallback((message, severity = 'info') => {
    setToast({
      open: true,
      message,
      severity,
    });
  }, []);

  const hideToast = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const showConfirm = useCallback(({ 
    title = 'Estás seguro?', 
    message = '', 
    confirmText = 'Aceptar', 
    cancelText = 'Cancelar', 
    danger = false, 
    onConfirm = () => {},
    onCancel = () => {}
  }) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      confirmText,
      cancelText,
      danger,
      onConfirm,
      onCancel,
    });
  }, []);

  const handleConfirmClose = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    confirmDialog.onCancel();
  }, [confirmDialog]);

  const handleConfirmAccept = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    confirmDialog.onConfirm();
  }, [confirmDialog]);

  return (
    <FeedbackContext.Provider value={{ showToast, showConfirm }}>
      {children}

      {/* --- TOAST COMPONENT --- */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={TransitionUp}
      >
        <Alert
          onClose={hideToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* --- CONFIRM DIALOG COMPONENT --- */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
            minWidth: { xs: '300px', sm: '400px' }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#525252' }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleConfirmClose} 
            color="inherit" 
            sx={{ fontWeight: 600, color: '#525252' }}
          >
            {confirmDialog.cancelText}
          </Button>
          <Button
            onClick={handleConfirmAccept}
            variant="contained"
            color={confirmDialog.danger ? 'error' : 'primary'}
            sx={{ fontWeight: 600, px: 3 }}
            disableElevation
          >
            {confirmDialog.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
};
